import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Search, QrCode, ArrowLeft, Wallet, Users, UserCheck, Plus, RefreshCw, Loader2, Camera, X, UserX } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ManageMembers from "./ManageMembers";
import ManageGuests from "../admin/ManageGuests";
import UnregisteredBuyers from "./UnregisteredBuyers";
import { PersonActivityCard } from "./PersonActivityCard";

export default function PeopleSection() {
  const [subTab, setSubTab] = useState("members");
  const [showScanner, setShowScanner] = useState(false);
  const [searchId, setSearchId] = useState("");
  const [scannedResult, setScannedResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => {
    let html5Qrcode = null;
    if (showScanner) {
      html5Qrcode = new Html5Qrcode("fa-qr-reader");
      html5Qrcode.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 220, height: 220 } },
        (decodedText) => { html5Qrcode.stop().then(() => { setShowScanner(false); handleQrScan(decodedText); }).catch(() => {}); }, () => {}
      ).catch(() => { toast.error("Camera access failed"); setShowScanner(false); });
    }
    return () => { if (html5Qrcode && html5Qrcode.isScanning) html5Qrcode.stop().catch(() => {}); };
  }, [showScanner]);

  const handleSearch = async () => {
    const id = searchId.trim();
    if (!id) return toast.error("Enter a member or guest ID");
    setScanLoading(true);
    try {
      const res = await authAxios.get(`/wallet/balance/${id}`);
      setScannedResult(res.data);
    } catch {
      toast.error("Account not found");
      setScannedResult(null);
    } finally { setScanLoading(false); }
  };

  const handleQrScan = async (scannedId) => {
    setShowScanner(false);
    const id = scannedId.trim();
    setSearchId(id);
    setScanLoading(true);
    try {
      const res = await authAxios.get(`/wallet/balance/${id}`);
      setScannedResult(res.data);
    } catch {
      toast.error(`Account not found: ${id}`);
      setScannedResult(null);
    } finally { setScanLoading(false); }
  };

  if (scannedResult) {
    return <ScannedResultView result={scannedResult} authAxios={authAxios} onBack={() => { setScannedResult(null); setSearchId(""); }} onRefresh={async (id) => {
      try { const res = await authAxios.get(`/wallet/balance/${id}`); setScannedResult(res.data); } catch (err) { console.error("Request failed:", err?.message || err); }
    }} />;
  }

  return (
    <div data-testid="people-section">
      {/* Unified search/scan bar */}
      <div className="bg-white rounded-2xl shadow-sm p-3 mb-4">
        <div className="flex gap-2">
          <input
            value={searchId}
            onChange={e => setSearchId(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search by ID (MM-26-... or MG-26-...)"
            className="flex-1 px-3 py-2.5 rounded-xl border text-sm font-mono"
            data-testid="people-search"
          />
          <button onClick={handleSearch} disabled={scanLoading} className="px-3 py-2.5 rounded-xl bg-[#1e3a5f] text-white" data-testid="people-search-btn">
            <Search className="w-5 h-5" />
          </button>
          <button onClick={() => setShowScanner(!showScanner)} className={`px-3 py-2.5 rounded-xl font-medium ${showScanner ? "bg-red-500 text-white" : "bg-[#2d8a4e] text-white"}`} data-testid="people-qr-btn">
            {showScanner ? <X className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
          </button>
        </div>
        {showScanner && (
          <div className="mt-3 rounded-xl overflow-hidden bg-white"><div id="fa-qr-reader" className="w-full"></div></div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSubTab("members")} className={`flex-1 min-w-0 px-1 py-2.5 rounded-xl text-xs sm:text-sm font-medium flex items-center justify-center gap-1 sm:gap-1.5 ${subTab === "members" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid="subtab-members">
          <UserCheck className="w-4 h-4" /> Members
        </button>
        <button onClick={() => setSubTab("guests")} className={`flex-1 min-w-0 px-1 py-2.5 rounded-xl text-xs sm:text-sm font-medium flex items-center justify-center gap-1 sm:gap-1.5 ${subTab === "guests" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid="subtab-guests">
          <Users className="w-4 h-4" /> Guests
        </button>
        <button onClick={() => setSubTab("unregistered")} className={`flex-1 min-w-0 px-1 py-2.5 rounded-xl text-xs sm:text-sm font-medium flex items-center justify-center gap-1 sm:gap-1.5 ${subTab === "unregistered" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid="subtab-unregistered">
          <UserX className="w-4 h-4" /> Unregistered
        </button>
      </div>

      {subTab === "members" && <ManageMembers />}
      {subTab === "guests" && <ManageGuests />}
      {subTab === "unregistered" && <UnregisteredBuyers />}
    </div>
  );
}


function ScannedResultView({ result, authAxios, onBack, onRefresh }) {
  const c = result.customer;
  const w = result.wallet;
  const [mode, setMode] = useState(null); // null | "topup" | "refund"
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [loading, setLoading] = useState(false);

  const handleTopup = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    setLoading(true);
    try {
      await authAxios.post("/wallet/topup", { customer_id: c.customer_id, amount: val, payment_type: paymentType });
      toast.success(`\u20ac${val.toFixed(2)} topped up!`);
      setMode(null); setAmount("");
      onRefresh(c.customer_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Top-up failed"); }
    finally { setLoading(false); }
  };

  const handleRefund = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    if (val > w.perpetual_balance) return toast.error("Exceeds refundable balance");
    setLoading(true);
    try {
      await authAxios.post("/wallet/refund", { customer_id: c.customer_id, amount: val });
      toast.success(`\u20ac${val.toFixed(2)} refunded!`);
      setMode(null); setAmount("");
      onRefresh(c.customer_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="scanned-result">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
        <div className="bg-[#1e3a5f] p-4 sm:p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"><Users className="w-6 h-6" /></div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate">{c.name}</h2>
              <p className="text-white/70 text-sm font-mono">{c.customer_id}</p>
              {c.email && <p className="text-white/50 text-xs truncate">{c.email}</p>}
              {c.is_active === false && <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">INACTIVE</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 divide-x text-center p-3 bg-slate-50">
          <div><p className="text-xl font-extrabold text-[#1e3a5f]">{"\u20ac"}{w.total_balance.toFixed(2)}</p><p className="text-slate-500 text-[10px]">Total</p></div>
          <div><p className="text-lg font-bold text-green-600">{"\u20ac"}{w.perpetual_balance.toFixed(2)}</p><p className="text-slate-500 text-[10px]">Perpetual</p></div>
          <div><p className="text-lg font-bold text-amber-600">{"\u20ac"}{(w.expiring_balance || 0).toFixed(2)}</p><p className="text-slate-500 text-[10px]">Expiring</p></div>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2 text-sm">
          {c.member_type_name && <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400 text-[10px]">Type</p><p className="font-medium text-xs">{c.member_type_name}</p></div>}
          {c.mobile_number && <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400 text-[10px]">Mobile</p><p className="font-medium text-xs">{c.mobile_number}</p></div>}
          {c.address && <div className="bg-slate-50 rounded-lg p-2 col-span-2"><p className="text-slate-400 text-[10px]">Address</p><p className="font-medium text-xs">{c.address}</p></div>}
          {c.last_membership_fee_paid_date && <div className="bg-green-50 rounded-lg p-2"><p className="text-slate-400 text-[10px]">Fee Paid</p><p className="font-medium text-xs text-green-600">{c.last_membership_fee_paid_date}</p></div>}
          {c.membership_start_date && <div className="bg-slate-50 rounded-lg p-2"><p className="text-slate-400 text-[10px]">Since</p><p className="font-medium text-xs">{c.membership_start_date}</p></div>}
        </div>
      </div>

      {/* Action buttons - only for active accounts */}
      {!mode && c.is_active !== false && (
        <div className="flex gap-2 mb-4">
          <button onClick={() => { setMode("topup"); setAmount(""); }} className="flex-1 py-3 rounded-xl bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2" data-testid="fa-topup-btn">
            <Plus className="w-5 h-5" /> Top-up
          </button>
          {w.perpetual_balance > 0 && (
            <button onClick={() => { setMode("refund"); setAmount(w.perpetual_balance.toFixed(2)); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold flex items-center justify-center gap-2" data-testid="fa-refund-btn">
              <RefreshCw className="w-5 h-5" /> Refund
            </button>
          )}
        </div>
      )}

      {/* Top-up form */}
      {mode === "topup" && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="fa-topup-form">
          <h3 className="font-bold text-[#1e3a5f] mb-3">Top-up Wallet</h3>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" className="w-full p-3 rounded-xl border text-lg font-bold text-center mb-3" data-testid="fa-topup-amount" min="0" step="0.01" />
          <div className="flex gap-2 mb-3">
            {["cash", "card", "bank_transfer"].map(pt => (
              <button key={pt} onClick={() => setPaymentType(pt)} className={`flex-1 py-2 rounded-lg text-xs font-medium ${paymentType === pt ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600"}`}>{pt === "bank_transfer" ? "Transfer" : pt.charAt(0).toUpperCase() + pt.slice(1)}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode(null)} className="flex-1 py-3 rounded-xl border font-bold text-slate-600">Cancel</button>
            <button onClick={handleTopup} disabled={loading} className="flex-1 py-3 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="fa-topup-confirm">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Top-up {"\u20ac"}{amount || "0"}
            </button>
          </div>
        </div>
      )}

      {/* Refund form */}
      {mode === "refund" && (
        <div className="bg-red-50 rounded-2xl p-4 mb-4" data-testid="fa-refund-form">
          <h3 className="font-bold text-red-700 mb-2">Refund Balance</h3>
          <p className="text-sm text-red-600 mb-3">Refundable: <span className="font-bold">{"\u20ac"}{w.perpetual_balance.toFixed(2)}</span></p>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Refund amount" className="w-full p-3 border border-red-200 rounded-xl text-lg font-bold text-center mb-3" data-testid="fa-refund-amount" min="0" step="0.01" />
          <div className="flex gap-2">
            <button onClick={() => setMode(null)} className="flex-1 py-3 rounded-xl border font-bold text-slate-600">Cancel</button>
            <button onClick={handleRefund} disabled={loading || !amount || parseFloat(amount) <= 0} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="fa-refund-confirm">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Refund {"\u20ac"}{amount || "0"}
            </button>
          </div>
        </div>
      )}

      <PersonActivityCard kind={c.customer_id?.startsWith("MG-") ? "guest" : "member"} id={c.customer_id} refreshKey={w.total_balance} />
    </div>
  );
}
